import React, { useRef, useEffect, useState, useCallback } from 'react';

function AddressAutocomplete({
  value,
  onAddressSelect,
  placeholder = "Street Address",
  className = "",
  required = false
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const isInitializedRef = useRef(false);
  const hasSelectedFromDropdownRef = useRef(false); // Track if user selected from Google dropdown
  const [localValue, setLocalValue] = useState(value || '');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [error, setError] = useState('');

  // Keep the callback ref updated without triggering re-initialization
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);



  /**
   * Handle blur event - accept manual/autofill input
   * When user fills field without selecting from Google dropdown,
   * just accept the value and update local state
   */
  const handleBlur = useCallback(() => {
    const inputValue = inputRef.current?.value?.trim();

    // Skip if:
    // - No value entered
    // - User already selected from Google dropdown
    // - Value hasn't changed from what we already processed
    if (
      !inputValue ||
      hasSelectedFromDropdownRef.current ||
      inputValue === localValue
    ) {
      return;
    }

    console.log('📱 Manual/autofill entry detected:', inputValue);

    // Just accept the value and update state
    // Let the form validation handle whether it's complete
    setLocalValue(inputValue);
    setError('');
  }, [localValue]);

  /**
   * Handle input change - reset the dropdown selection flag
   */
  const handleInputChange = useCallback(() => {
    // User is typing manually, reset the flag
    hasSelectedFromDropdownRef.current = false;
  }, []);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMaps()) {
      return;
    }

    // If not loaded, check every 100ms for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds
    const interval = setInterval(() => {
      attempts++;
      if (checkGoogleMaps() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts) {
          console.error('Google Maps API failed to load after 10 seconds');
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Update local value when prop changes (from external updates)
  useEffect(() => {
    if (value !== undefined && value !== localValue) {
      setLocalValue(value);
      if (inputRef.current) {
        inputRef.current.value = value;
      }
    }
  }, [value, localValue]);

  useEffect(() => {
    // Wait for Google Maps to be loaded
    if (!isGoogleLoaded || !inputRef.current) {
      return;
    }

    // CRITICAL: Only initialize ONCE to prevent multiple API calls
    // This was causing 7000+ API calls/day by re-initializing on every render
    if (isInitializedRef.current && autocompleteRef.current) {
      return;
    }

    try {
      // Initialize autocomplete widget (handles sessions automatically)
      // IMPORTANT: Do NOT add sessionToken parameter - it's not supported by Autocomplete widget
      // The widget automatically manages sessions for optimal billing (per-session, not per-request)
      // REMOVED 'geometry' field to reduce API costs - we don't need lat/lng coordinates
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address'] // Removed 'geometry' to save costs
        }
      );

      isInitializedRef.current = true;

      // Handle place selection from Google dropdown
      const listener = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        // Mark that user selected from dropdown (not mobile autofill)
        hasSelectedFromDropdownRef.current = true;

        if (!place.address_components) {
          console.log('No address components found');
          setError('Please select a valid address from the dropdown');
          return;
        }

        // Parse address components
        const addressData = {
          streetAddress: '',
          city: '',
          state: '',
          postalCode: '',
          fullAddress: place.formatted_address
        };

        let streetNumber = '';
        let route = '';
        let subpremise = ''; // Apartment/unit number

        place.address_components.forEach(component => {
          const type = component.types[0];

          switch (type) {
            case 'street_number':
              streetNumber = component.long_name;
              break;
            case 'route':
              route = component.long_name;
              break;
            case 'subpremise':
              subpremise = component.long_name;
              break;
            case 'locality':
              addressData.city = component.long_name;
              break;
            case 'administrative_area_level_1':
              addressData.state = component.short_name;
              break;
            case 'postal_code':
              addressData.postalCode = component.long_name;
              break;
            default:
              break;
          }
        });

        // Build street address - MUST have street number or route
        if (streetNumber || route) {
          addressData.streetAddress = `${streetNumber} ${route}`.trim();
          setError(''); // Clear any previous errors
        } else {
          // No valid street address found
          setError('Please enter a complete street address (not just apartment/unit number)');
          console.warn('⚠️ Incomplete address - missing street name:', place);
          if (inputRef.current) {
            inputRef.current.value = '';
          }
          setLocalValue('');
          return; // Don't call onAddressSelect with incomplete data
        }

        // Log for debugging
        console.log('📍 Address parsed:', {
          streetNumber,
          route,
          subpremise,
          final: addressData.streetAddress,
          city: addressData.city,
          state: addressData.state,
          zip: addressData.postalCode
        });

        // Update the input to show the street address (without subpremise)
        if (inputRef.current) {
          inputRef.current.value = addressData.streetAddress;
        }

        // Update local state
        setLocalValue(addressData.streetAddress);

        // Call the callback with parsed address data using ref (prevents re-initialization)
        // Note: subpremise should be captured separately as street_address_2
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current({
            ...addressData,
            subpremise // Pass subpremise separately so it can go to street_address_2
          });
        }
      });

      // Store listener ref for cleanup
      listenerRef.current = listener;

      // Cleanup
      return () => {
        if (listenerRef.current) {
          window.google.maps.event.removeListener(listenerRef.current);
          listenerRef.current = null;
        }
        // Reset initialization flag if component unmounts
        isInitializedRef.current = false;
        autocompleteRef.current = null;
      };
    } catch (error) {
      console.error('Error initializing Google Maps Autocomplete:', error);
    }
  }, [isGoogleLoaded]); // REMOVED onAddressSelect from dependencies - using ref instead

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        name="streetAddress"
        defaultValue={localValue}
        placeholder={placeholder}
        className={`${className} ${error ? 'border-red-500' : ''}`}
        required={required}
        autoComplete="street-address"
        style={{ color: '#111827' }}
        onBlur={handleBlur}
        onChange={handleInputChange}
      />
      {error && (
        <div className="text-xs text-red-600 mt-1 font-medium">
          {error}
        </div>
      )}
      {!isGoogleLoaded && (
        <div className="text-xs text-gray-500 mt-1">
          Loading address autocomplete...
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;